import { randomUUID } from 'node:crypto';
import type { AppManifest, LooseAppManifest } from '@wix/cli-app-manifest';
import type { HttpClient, HttpError } from '@wix/cli-http-client';
import type { Options } from 'p-retry';
import { release } from '@wix/ambassador-ctp-gradual-rollout-v1-baas-release/http';
import {
  getLatestProductionVersion,
  promoteAppVersionFromDraftToProduction,
  suggestNextVersions,
} from '@wix/ambassador-devcenter-apps-v1-app-version/http';
import { PromoteStrategy } from '@wix/ambassador-devcenter-apps-v1-app-version/types';
import {
  commitOverride,
  createComponentsOverride,
  getComponentsOverride,
  updateComponentsOverrides,
} from '@wix/ambassador-devcenter-components-overrides-v1-components-override/http';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isHttpError } from '@wix/cli-http-client';
import { getTestOverrides } from '@wix/cli-test-overrides';
import pRetry from 'p-retry';
import { fields, variant } from 'variant';
import {
  getComponentsOverrideResponseSchema,
  getLatestProductionVersionResponseSchema,
  promoteAppVersionFromDraftResponseSchema,
  releaseResponseSchema,
  suggestNextVersionsResponseSchema,
} from './schemas.js';
import { handleReleaseError, toModifiedComponents } from './utils.js';

const UpdateAppReleaseData = variant({
  TagNotFound: fields<{ cause: HttpError }>(),
  TagUpdated: {},
});

export class DevCenterOverridesClient {
  private readonly retryOptions: Options;

  constructor(private readonly httpClient: HttpClient) {
    const { minRetryTimeout, retries } = getTestOverrides();

    this.retryOptions = {
      maxTimeout: 3 * 1000,
      minTimeout: minRetryTimeout,
      retries: retries ?? 3,
    };
  }

  private getAppLatestVersion = async (appId: string): Promise<number> => {
    try {
      const { data } = await this.httpClient.request(
        getLatestProductionVersion({ appId })
      );
      return getLatestProductionVersionResponseSchema.parse(data).appVersion
        .version;
    } catch {
      return 0;
    }
  };

  createAppRelease = async (
    manifest: AppManifest,
    projectId: string
  ): Promise<string> => {
    const overrideId = randomUUID();
    const appVersion = await this.getAppLatestVersion(manifest.appId);

    try {
      await pRetry(
        () =>
          this.httpClient.request(
            createComponentsOverride({
              componentsOverride: {
                appId: manifest.appId,
                appVersion,
                externalId: projectId,
                id: overrideId,
                modifiedComponents: toModifiedComponents(manifest.components),
              },
            })
          ),
        this.retryOptions
      );

      return overrideId;
    } catch (e) {
      handleReleaseError(
        e,
        manifest.appId,
        CliErrorCode.FailedToCreateAppRelease()
      );
    }
  };

  createLooseAppRelease = async (
    manifest: LooseAppManifest,
    projectId: string
  ) => {
    return this.createAppRelease(manifest as AppManifest, projectId);
  };

  updateAppRelease = async (manifest: AppManifest, tag: string) => {
    try {
      const appVersion = await this.getAppLatestVersion(manifest.appId);

      const { data: overrideData } = await this.httpClient.request(
        getComponentsOverride({ componentsOverrideId: tag })
      );
      const { componentsOverride } =
        getComponentsOverrideResponseSchema.parse(overrideData);

      await pRetry(
        () =>
          this.httpClient.request(
            updateComponentsOverrides({
              componentsOverride: {
                appId: manifest.appId,
                appVersion,
                externalId: componentsOverride.externalId,
                id: tag,
                modifiedComponents: toModifiedComponents(manifest.components),
              },
              fieldMask: ['modifiedComponents', 'appVersion'],
            })
          ),
        this.retryOptions
      );

      return UpdateAppReleaseData.TagUpdated();
    } catch (e) {
      if (isHttpError(e) && e.response?.status === 404) {
        return UpdateAppReleaseData.TagNotFound({ cause: e });
      }
      handleReleaseError(
        e,
        manifest.appId,
        CliErrorCode.FailedToUpdateAppRelease()
      );
    }
  };

  updateLooseAppRelease = async (manifest: LooseAppManifest, tag: string) => {
    return this.updateAppRelease(manifest as AppManifest, tag);
  };

  createDraftVersion = async (appId: string, tag: string) => {
    try {
      await this.httpClient.request(
        commitOverride({ componentsOverrideId: tag, onlyEmitEvent: false })
      );

      const { data } = await this.httpClient.request(
        suggestNextVersions({ appId })
      );

      const { nextMajor, nextMinor } =
        suggestNextVersionsResponseSchema.parse(data);

      return { nextMajor, nextMinor };
    } catch (cause) {
      throw new CliError({
        cause,
        code: CliErrorCode.FailedToCreateDraftVersion(),
      });
    }
  };

  releaseDraftVersion = async (
    appId: string,
    options: {
      createNewMajor: boolean;
      latestMajor: number;
      notes: string;
    }
  ) => {
    try {
      const { data } = await this.httpClient.request(
        promoteAppVersionFromDraftToProduction({
          appId,
          createNewMajor: options.createNewMajor,
          notes: options.notes,
          promoteStrategy: PromoteStrategy.ENTIRE_APP,
          version: options.latestMajor,
        })
      );

      const { appVersion } =
        promoteAppVersionFromDraftResponseSchema.parse(data);

      return { releasedVersion: appVersion.fullVersion ?? '' };
    } catch (cause) {
      throw new CliError({
        cause,
        code: CliErrorCode.FailedToReleaseDraftVersion(),
      });
    }
  };

  release = async (
    appId: string,
    componentOverrideId: string,
    options: {
      comment?: string;
      createMinorVersion: boolean;
    }
  ) => {
    try {
      const { comment, createMinorVersion } = options;

      const { data } = await this.httpClient.request(
        release({
          appId,
          componentOverrideId,
          createMinorVersion,
          notes: comment,
        })
      );

      return releaseResponseSchema.parse(data).releaseBaseUrl;
    } catch (e) {
      throw new CliError({
        cause: e,
        code: CliErrorCode.FailedToReleaseHeadlessProject(),
      });
    }
  };
}
